import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronRight, ArrowRight, Quote } from "lucide-react";
import { useSeo } from "@/lib/seo";

const FOUNDER_IMG = "https://static.prod-images.emergentagent.com/jobs/1c646e73-3991-4deb-9e9d-27e0d854e41a/images/3b3f5e8fc31e07d8ebf629d08bb7922b804307aa1d07c4f44d2585f915a43638.jpeg";

const PARAGRAPHS = [
  "For more than two decades, I've built my career around people, creativity, and making things that matter. That same mindset led me into entrepreneurship and, ultimately, to Collins Crafty Creations.",
  "What began as a creative outlet became a calling.",
  "In 2025, I launched Collins Crafty Creations, turning my love for design and handmade craft into a growing business built around one idea: that the things we wear, gift, and bring into our homes should feel personal.",
  "Every piece is made to order.",
  "From custom apparel and tumblers to personalized gifts, resin art, and bespoke printing, each creation is handcrafted with care — designed around your story, your occasion, and your vision.",
  "I believe a product should mean something. Whether it's a shirt that says exactly what you feel, a gift that lands just right, or a custom piece for a big moment, the goal is always the same — to create something uniquely yours.",
  "Thank you for supporting handmade. Let's create something together.",
];

export default function About() {
  useSeo("About Erika Collins — Collins Crafty Creations | Memphis, TN", "Meet Erika Collins, the maker behind Collins Crafty Creations — handmade custom apparel, personalized gifts, and bespoke printing crafted in Memphis, TN.");
  return (
    <>
      <div className="bg-slate-50 border-b border-silver-light">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 text-xs text-navy/60">
          <Link to="/" className="hover:text-navy-royal">Home</Link><ChevronRight size={12} />
          <span className="text-navy-dark font-semibold">About</span>
        </div>
      </div>

      {/* Hero: photo + intro */}
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-12" data-testid="about-page">
        <div className="grid lg:grid-cols-[420px_1fr] gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="relative mx-auto lg:mx-0">
            <div className="absolute -inset-4 rounded-[2rem] bg-navy-royal/10 -z-10" aria-hidden="true" />
            <div className="rounded-[2rem] overflow-hidden bg-gradient-to-b from-navy-dark to-navy-royal shadow-2xl">
              <img src={FOUNDER_IMG} alt="Erika Collins, maker behind Collins Crafty Creations" className="w-full h-auto object-cover" data-testid="founder-photo" />
            </div>
            <div className="mt-4 text-center lg:text-left">
              <div className="font-serif text-2xl text-navy-dark">Erika Collins</div>
              <div className="text-xs uppercase tracking-[0.2em] text-navy-royal font-semibold mt-1">Founder & Maker</div>
            </div>
          </motion.div>

          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-navy-royal font-semibold">About Me</div>
            <div className="font-script text-5xl sm:text-6xl text-navy-royal leading-none mt-2">Purpose. Creativity. Impact.</div>
            <p className="text-lg text-navy/75 mt-6 leading-relaxed">
              I'm <strong className="text-navy-dark">Erika Collins</strong>, the maker behind Collins Crafty Creations — built from a simple belief: the things we wear, gift, and treasure should feel personal and made with heart.
            </p>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="max-w-3xl mx-auto px-6 pb-8">
        <div className="space-y-5 text-navy/75 leading-relaxed">
          {PARAGRAPHS.map((p, i) => (
            <motion.p key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.05 }}
              className={i === 1 || i === 3 || i === 6 ? "font-serif text-2xl text-navy-dark !leading-snug" : ""}>
              {p}
            </motion.p>
          ))}
        </div>
      </section>

      {/* Pull quote */}
      <section className="max-w-4xl mx-auto px-6 py-12">
        <div className="rounded-3xl bg-navy-dark text-white p-10 sm:p-14 relative overflow-hidden">
          <Quote size={80} className="absolute -top-3 -left-2 text-white/5" />
          <div className="relative">
            <p className="text-white/85">Whether I'm creating a product, developing a new business concept, sharing knowledge, supporting another entrepreneur, or bringing an idea from vision to reality, my goal remains the same:</p>
            <div className="font-script text-3xl sm:text-4xl text-white mt-6 leading-snug">
              Create with purpose. Lead with intention. Build something that matters.
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="max-w-6xl mx-auto px-6 pb-12 grid md:grid-cols-3 gap-6">
        {[
          { t: "Purpose", body: "Every idea, product, and service starts with a reason that matters and a person it can serve." },
          { t: "Creativity", body: "From personalized creations to new business concepts, imagination turns vision into something real." },
          { t: "Impact", body: "Handmade in Memphis — thoughtful, quality craft that turns everyday items into something personal." },
        ].map((c, i) => (
          <motion.div key={c.t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
            className="p-8 rounded-2xl bg-white border border-silver-light border-t-4 border-t-navy-royal/40">
            <div className="text-xs tracking-[0.2em] uppercase text-navy-royal font-bold">0{i + 1}</div>
            <div className="font-serif text-2xl text-navy-dark mt-2">{c.t}</div>
            <p className="text-navy/70 mt-3 leading-relaxed text-sm">{c.body}</p>
          </motion.div>
        ))}
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="rounded-3xl bg-slate-50 border border-silver-light p-12 text-center">
          <p className="text-navy/70 max-w-2xl mx-auto">Welcome to Collins Crafty Creations — where every piece is handmade to order and crafted just for you.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/shop" className="inline-flex items-center gap-2 rounded-md bg-navy-dark text-white px-8 py-4 text-xs font-bold uppercase tracking-[0.18em] hover:bg-navy-royal transition-colors" data-testid="about-shop-btn">
              Shop the Collection <ArrowRight size={14} />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-md border border-navy-dark text-navy-dark px-8 py-4 text-xs font-bold uppercase tracking-[0.18em] hover:bg-navy-dark hover:text-white transition-colors" data-testid="about-contact-btn">
              Get In Touch
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
